import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, KeyRound, Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ActivateAccount() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/activation/${token}`)
      .then(({ data }) => setState({ status: data.status || "open", ...data }))
      .catch((err) => setState({ status: "error", detail: err?.response?.data?.detail || "Error" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Mínimo 8 caracteres");
    if (password !== confirm) return toast.error("Las contraseñas no coinciden");
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/activation/${token}`, { password });
      setState({ status: "done", email: state.email });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo activar");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <Logo size={32} withText />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md" data-testid="activate-page">
          <CardContent className="p-8 space-y-5">
            <div className="flex items-center gap-3">
              <KeyRound className="w-7 h-7 text-primary" />
              <div>
                <div className="font-display text-xl">Activar cuenta paciente</div>
                <div className="text-xs text-muted-foreground">Elija una contraseña para acceder al portal.</div>
              </div>
            </div>

            {state.status === "loading" && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Comprobando enlace...</div>
            )}
            {(state.status === "used" || state.status === "expired") && (
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  {state.status === "used" ? "Este enlace ya se ha utilizado." : "Este enlace ha caducado."} Contacte con la consulta para recibir uno nuevo.
                </div>
              </div>
            )}
            {state.status === "error" && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm">Enlace no válido: {state.detail}</div>
            )}
            {state.status === "open" && (
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Correo electrónico</Label>
                  <Input value={state.email || ""} disabled />
                </div>
                <div>
                  <Label>Nueva contraseña *</Label>
                  <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="activate-password" />
                </div>
                <div>
                  <Label>Repita la contraseña *</Label>
                  <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="activate-confirm" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-primary hover:bg-primary/90" data-testid="activate-submit">
                  {submitting ? "Activando..." : "Activar cuenta"}
                </Button>
              </form>
            )}
            {state.status === "done" && (
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm flex items-start gap-2" data-testid="activate-done">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Cuenta activada correctamente</div>
                  <div className="text-xs text-muted-foreground mt-1">Ya puede iniciar sesión con su correo y la contraseña que acaba de establecer.</div>
                  <a href="/login" className="mt-2 inline-block text-primary underline text-sm">Ir al inicio de sesión</a>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
